import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { 
  Phone, 
  PhoneCall,
  PhoneOff,
  MessageSquare, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Mic,
  MicOff,
  Volume2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Call Button Component
export function CallButton({ 
  phoneNumber, 
  contactId, 
  contactType, 
  contactName,
  size = "default",
  className = ""
}) {
  const [calling, setCalling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleCall = async () => {
    setCalling(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/dialer/call`,
        {
          to_number: phoneNumber,
          contact_id: contactId,
          contact_type: contactType,
          contact_name: contactName
        },
        { withCredentials: true }
      );

      if (response.data.demo_mode) {
        toast.info("Demo Mode: Call simulated. Connect Twilio for real calls.");
      } else if (response.data.success) {
        toast.success(`Calling ${contactName}...`);
      } else {
        toast.error(response.data.error || "Failed to initiate call");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to make call");
    } finally {
      setCalling(false);
      setShowConfirm(false);
    }
  };

  const buttonSize = size === "sm" ? "h-8 w-8 p-0" : "px-3";

  return (
    <>
      <Button
        onClick={() => setShowConfirm(true)}
        disabled={!phoneNumber || calling}
        className={`bg-emerald-600 hover:bg-emerald-500 text-white rounded-none ${buttonSize} ${className}`}
        title={phoneNumber ? `Call ${phoneNumber}` : "No phone number"}
        data-testid={`call-btn-${contactId}`}
      >
        <Phone className={size === "sm" ? "w-4 h-4" : "w-4 h-4 mr-2"} />
        {size !== "sm" && "Call"}
      </Button>

      {/* TCPA Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="bg-[#0A0A0A] border-white/[0.08] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Phone className="w-5 h-5 text-emerald-400" />
              Confirm Call
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-black border border-white/[0.08] p-4">
              <p className="text-white font-medium">{contactName}</p>
              <p className="text-white/70 text-sm">{phoneNumber}</p>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 p-3 text-sm">
              <p className="text-amber-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                TCPA Compliance Reminder
              </p>
              <p className="text-amber-300/80 text-xs mt-1">
                Ensure you have consent to contact this number. Calls may be recorded.
              </p>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirm(false)}
                className="flex-1 border-white/[0.1] text-white/70 rounded-none"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCall}
                disabled={calling}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-none font-bold"
              >
                {calling ? "Connecting..." : "Start Call"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// SMS Button Component
export function SMSButton({ 
  phoneNumber, 
  contactId, 
  contactType, 
  contactName,
  size = "default",
  className = ""
}) {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  useEffect(() => {
    if (showModal) {
      fetchTemplates();
    }
  }, [showModal]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/dialer/templates`,
        { withCredentials: true }
      );
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const handleTemplateSelect = async (templateId) => {
    setSelectedTemplate(templateId);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/dialer/templates/render`,
        {
          template_id: templateId,
          variables: {
            name: contactName || "there",
            property: "the property"
          }
        },
        { withCredentials: true }
      );
      setMessage(response.data.message);
    } catch (error) {
      console.error("Failed to render template:", error);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/dialer/sms`,
        {
          to_number: phoneNumber,
          message: message,
          contact_id: contactId,
          contact_type: contactType,
          contact_name: contactName,
          template_id: selectedTemplate || null
        },
        { withCredentials: true }
      );

      if (response.data.demo_mode) {
        toast.info("Demo Mode: SMS simulated. Connect Twilio for real messages.");
      } else if (response.data.success) {
        toast.success(`SMS sent to ${contactName}`);
      } else {
        toast.error(response.data.error || "Failed to send SMS");
      }
      
      setShowModal(false);
      setMessage("");
      setSelectedTemplate("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSending(false);
    }
  };

  const buttonSize = size === "sm" ? "h-8 w-8 p-0" : "px-3";

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        disabled={!phoneNumber}
        className={`bg-blue-600 hover:bg-blue-500 text-white rounded-none ${buttonSize} ${className}`}
        title={phoneNumber ? `SMS ${phoneNumber}` : "No phone number"}
        data-testid={`sms-btn-${contactId}`}
      >
        <MessageSquare className={size === "sm" ? "w-4 h-4" : "w-4 h-4 mr-2"} />
        {size !== "sm" && "SMS"}
      </Button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/[0.08] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-400" />
              Send SMS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-black border border-white/[0.08] p-3">
              <p className="text-white font-medium text-sm">{contactName}</p>
              <p className="text-white/70 text-xs">{phoneNumber}</p>
            </div>

            {templates.length > 0 && (
              <div className="space-y-2">
                <Label className="text-white/70">Quick Templates</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger className="bg-black border-white/[0.08] text-white">
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/[0.08]">
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id} className="text-white hover:bg-white/[0.06]">
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-white/70">Message</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="bg-black border-white/[0.08] text-white min-h-[100px]"
                placeholder="Type your message..."
                maxLength={160}
              />
              <p className="text-xs text-white/70 text-right">{message.length}/160</p>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowModal(false)}
                className="flex-1 border-white/[0.1] text-white/70 rounded-none"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSend}
                disabled={sending || !message.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-none font-bold"
              >
                {sending ? "Sending..." : "Send SMS"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Combined Dialer Actions Component
export function DialerActions({ 
  phoneNumber, 
  contactId, 
  contactType, 
  contactName,
  size = "default",
  showLabels = true
}) {
  return (
    <div className="flex gap-2">
      <CallButton
        phoneNumber={phoneNumber}
        contactId={contactId}
        contactType={contactType}
        contactName={contactName}
        size={showLabels ? "default" : "sm"}
      />
      <SMSButton
        phoneNumber={phoneNumber}
        contactId={contactId}
        contactType={contactType}
        contactName={contactName}
        size={showLabels ? "default" : "sm"}
      />
    </div>
  );
}

// Call History Component
export function CallHistory({ contactId, contactType, limit = 10 }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCallHistory();
  }, [contactId]);

  const fetchCallHistory = async () => {
    try {
      const params = new URLSearchParams();
      if (contactId) params.append("contact_id", contactId);
      if (contactType) params.append("contact_type", contactType);
      params.append("limit", limit.toString());

      const response = await axios.get(
        `${BACKEND_URL}/api/dialer/history/calls?${params.toString()}`,
        { withCredentials: true }
      );
      setCalls(response.data.calls || []);
    } catch (error) {
      console.error("Failed to fetch call history:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case "no-answer":
      case "busy":
        return <PhoneOff className="w-4 h-4 text-amber-400" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-400" />;
      case "demo":
        return <AlertCircle className="w-4 h-4 text-blue-400" />;
      default:
        return <Phone className="w-4 h-4 text-white/70" />;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="text-center p-4 text-white/70 text-sm">
        <Phone className="w-8 h-8 mx-auto mb-2 opacity-50" />
        No call history
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {calls.map((call) => (
        <div 
          key={call.call_id} 
          className="flex items-center justify-between p-3 bg-black border border-white/[0.08]"
        >
          <div className="flex items-center gap-3">
            {getStatusIcon(call.status)}
            <div>
              <p className="text-sm text-white">{call.to_number}</p>
              <p className="text-xs text-white/70">
                {new Date(call.created_at).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-white/70">{formatDuration(call.duration_seconds)}</p>
            {call.recording_url && (
              <a 
                href={call.recording_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-gold hover:text-gold-light"
              >
                <Volume2 className="w-3 h-3 inline mr-1" />
                Recording
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// AI Usage Display Component
export function AIUsageDisplay({ compact = false }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/ai/usage`,
        { withCredentials: true }
      );
      setUsage(response.data);
    } catch (error) {
      console.error("Failed to fetch AI usage:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !usage) {
    return null;
  }

  if (!usage.has_ai_access) {
    return (
      <div className="bg-[#0A0A0A] border border-white/[0.08] p-4">
        <p className="text-white/70 text-sm">AI features are available on Pro and above.</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-white/70">AI Usage:</span>
        <span className="text-white font-mono">
          {usage.total.used}/{usage.total.limit}
        </span>
        {usage.total.percentage >= 80 && (
          <span className="text-amber-400 text-xs">(Warning)</span>
        )}
      </div>
    );
  }

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-bold uppercase text-sm">AI Usage</h3>
        <span className="text-xs text-white/70">{usage.month}</span>
      </div>

      {/* Total Progress Bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-white/70">Total</span>
          <span className="text-xs text-white font-mono">
            {usage.total.used} / {usage.total.limit}
          </span>
        </div>
        <div className="h-2 bg-white/[0.08] rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${
              usage.total.percentage >= 80 ? "bg-amber-500" : "bg-gold"
            }`}
            style={{ width: `${Math.min(100, usage.total.percentage)}%` }}
          />
        </div>
      </div>

      {/* Breakdown by type */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        {Object.entries(usage.usage).map(([type, data]) => (
          <div key={type} className="flex items-center justify-between">
            <span className="text-white/70 capitalize">{type.replace("_", " ")}</span>
            <span className="text-white font-mono">{data.used}/{data.limit}</span>
          </div>
        ))}
      </div>

      {/* Boost credits */}
      {usage.boost_available > 0 && (
        <div className="pt-2 border-t border-white/[0.08]">
          <span className="text-xs text-emerald-400">
            +{usage.boost_available} boost credits available
          </span>
        </div>
      )}

      {/* Warning */}
      {usage.warning && (
        <div className="bg-amber-500/10 border border-amber-500/30 p-2 text-xs text-amber-400">
          {usage.warning}
        </div>
      )}
    </div>
  );
}

export default { CallButton, SMSButton, DialerActions, CallHistory, AIUsageDisplay };
